import { LocalVideoTrack, Room } from 'twilio-video';
import { useState, useEffect, useCallback } from 'react';
import { SELECTED_BACKGROUND_SETTINGS_KEY } from '../../../constants';
import { useAppSelector } from '../../../redux/hooks'
import {
  GaussianBlurBackgroundProcessor,
  VirtualBackgroundProcessor,
  ImageFit,
  isSupported,
} from '@twilio/video-processors';
import { Thumbnail } from '../../BackgroundSelectionDialog/BackgroundThumbnail/BackgroundThumbnail';

export interface BackgroundSettings {
  type: Thumbnail;
  index?: number;
}

const getImage = async (url: string): Promise<any> => {
  const img = new Image();
  // Required for remote S3 images, otherwise the background processor's canvas goes black.
  img.crossOrigin = 'anonymous';
  return new Promise((resolve, reject) => {
    img.onload = () => {
      resolve(img);
    };
    img.onerror = reject;
    img.src = url;
  });
};

// Backgrounds come from the JWT (Order::getTokenVideoChat already merges
// company custom entries + defaults, in order).
export interface VirtualBackground {
  label: string;
  url: string;
  thumb_url: string;
}

export const getBackgroundConfig = (virtualBackgrounds: VirtualBackground[] = []) => ({
  imageNames: virtualBackgrounds.map(bg => bg.label),
  images: virtualBackgrounds.map(bg => bg.thumb_url),
});


const virtualBackgroundAssets = '/';
let blurProcessor: GaussianBlurBackgroundProcessor;
let virtualBackgroundProcessor: VirtualBackgroundProcessor;

export default function useBackgroundSettings(videoTrack: LocalVideoTrack | undefined, room?: Room | null) {
  const collection: any = useAppSelector(state => state.collection.list);
  const virtualBackgrounds: VirtualBackground[] = collection.tokenData?.decode?.virtualBackgrounds || [];

  const [backgroundSettings, setBackgroundSettings] = useState<BackgroundSettings>(() => {
    const localStorageSettings = localStorage.getItem(SELECTED_BACKGROUND_SETTINGS_KEY);
    return localStorageSettings ? JSON.parse(localStorageSettings) : { type: 'none', index: 0 };
  });

  const removeProcessor = useCallback(() => {
    if (videoTrack && videoTrack.processor) {
      videoTrack.removeProcessor(videoTrack.processor);
    }
  }, [videoTrack]);

  const addProcessor = useCallback(
    (processor: GaussianBlurBackgroundProcessor | VirtualBackgroundProcessor) => {
      if (!videoTrack || videoTrack.processor === processor) {
        return;
      }
      removeProcessor();
      videoTrack.addProcessor(processor);
    },
    [videoTrack, removeProcessor]
  );

  useEffect(() => {
    if (!isSupported) {
      return;
    }

    // make sure localParticipant has joined room before applying video processors
    // this ensures that the video processors are not applied on the LocalVideoPreview
    const handleProcessorChange = async () => {
      if (!blurProcessor) {
        blurProcessor = new GaussianBlurBackgroundProcessor({
          assetsPath: virtualBackgroundAssets,
        });
        await blurProcessor.loadModel();
      }
     
      
      // virtualBackgrounds comes from the JWT and may not have loaded yet on
      // the first pass, so wait for it before constructing the processor —
      // its backgroundImage can't be null.
      if (!virtualBackgroundProcessor && virtualBackgrounds[0]?.url) {
        virtualBackgroundProcessor = new VirtualBackgroundProcessor({
          assetsPath: virtualBackgroundAssets,
          backgroundImage: await getImage(virtualBackgrounds[0].url),
          fitType: ImageFit.Fill,
        });
        await virtualBackgroundProcessor.loadModel();
      }
      if (!room?.localParticipant) {
        return;
      }

      if (backgroundSettings.type === 'blur') {
        addProcessor(blurProcessor);
      } else if (
        virtualBackgroundProcessor &&
        backgroundSettings.type === 'image' &&
        typeof backgroundSettings.index === 'number' &&
        virtualBackgrounds[backgroundSettings.index]?.url
      ) {
        virtualBackgroundProcessor.backgroundImage = await getImage(virtualBackgrounds[backgroundSettings.index].url);
        addProcessor(virtualBackgroundProcessor);
      } else {
        removeProcessor();
      }
    };
    handleProcessorChange();
    localStorage.setItem(SELECTED_BACKGROUND_SETTINGS_KEY, JSON.stringify(backgroundSettings));
  }, [backgroundSettings, videoTrack, room, addProcessor, removeProcessor, collection, virtualBackgrounds]);

  return [backgroundSettings, setBackgroundSettings] as const;
}
